import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { Box, LinearProgress } from '@mui/material';
import { format } from 'date-fns';
import useInitMasonryColumns from 'hooks/useInitMasonryColumns';
import type { GoogleCreative } from '../api/types';
import CreativeEmptyView from '../../creatives/components/CreativeEmptyView';
import useFacebookAdsLibraryParams from '../hooks/useFacebookAdsLibraryParams';
import { useGoogleCreatives } from '../api';
import GoogleCreativeItem from './GoogleCreativeItem';

const PAGE_SIZE = 50;

interface IProps {
  resetFilters: () => void;
}

const GoogleAdsCreativeView: FC<IProps> = ({ resetFilters }) => {
  const {
    company,
    domains,
    advertiserIds,
    googleAdTypes,
    ordering,
    startDate,
    endDate,
    newOnly,
    lastShownRecently,
    includeWelltech,
    network,
    region,
  } = useFacebookAdsLibraryParams();

  const {
    data: { pages, count },
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useGoogleCreatives({
    date_from: format(startDate, 'yyyy-MM-dd'),
    date_to: format(endDate, 'yyyy-MM-dd'),
    desc: ordering.split(':')[1] === 'desc',
    limit: PAGE_SIZE,
    order_by: ordering.split(':')[0],
    offset: 0,
    company_id: company,
    domain: domains,
    advertiser_id: advertiserIds,
    format: googleAdTypes,
    new_only: newOnly,
    last_shown_recently: lastShownRecently,
    include_welltech: includeWelltech,
    network,
    region,
  });

  const { columns } = useInitMasonryColumns();

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage().catch(console.error);
        }
      },
      { root: document.querySelector('#creatives-ads-page'), rootMargin: '600px' }
    );

    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const creativePageContainer = document.querySelector('#creatives-ads-page');
    if (creativePageContainer) creativePageContainer.scrollTop = 0;
  }, [ordering, company, domains, advertiserIds, googleAdTypes, startDate, endDate, newOnly, lastShownRecently, includeWelltech, network, region]);

  const columnBuckets: GoogleCreative[][] = Array.from({ length: columns }, () => []);
  const allCreatives: GoogleCreative[] = pages.flat();
  allCreatives.forEach((creative, index) => {
    columnBuckets[index % columns].push(creative);
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {count ? (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          {columnBuckets.map((bucket, columnIndex) => (
            <Box
              key={`google-creatives-column-${columnIndex}`}
              sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              {bucket.map((c) => (
                <GoogleCreativeItem key={c.adCreativeId} creative={c} />
              ))}
            </Box>
          ))}
        </Box>
      ) : (
        <CreativeEmptyView reset={resetFilters} />
      )}
      <Box ref={sentinelRef} sx={{ height: 1 }} />
      {isFetchingNextPage ? (
        <Box sx={{ py: 2 }}>
          <LinearProgress />
        </Box>
      ) : null}
    </Box>
  );
};

export default GoogleAdsCreativeView;
